import React, { useContext, useEffect, useState } from 'react';
import {
    CompoundButton,
    DefaultButton,
    DirectionalHint,
    IButtonStyles,
    IContextualMenuItem,
    IContextualMenuProps,
} from '@fluentui/react';
import { AuthContext } from '../../models/AuthContext';
import { UserPersona } from './Display/UserPersona';
import { ThemeContext } from '../../models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';
import { UserRolesModal } from './UserRolesModal';
import { useUserRoles } from '../../../Common/hooks/useUserRoles';
import { HubIdTokenClaims } from '../../util/authUtils';

export const UserProfile: React.FC = () => {
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);

    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const { authContext } = useContext(AuthContext);
    const upn = (authContext.user?.idTokenClaims as HubIdTokenClaims)?.upn;
    const username =
        (Array.isArray(upn) ? upn[0] : upn) ?? authContext.user?.username ?? '';
    const userRoles = useUserRoles();

    //if we have authenticated the user, get the user profile photo
    useEffect(() => {
        if (authContext.isUserAuthorized && username && !imageUrl) {
            //getPhoto(username).then((response) => {
            //    setImageUrl(URL.createObjectURL(response));
            //});
        }
    }, [authContext.isUserAuthorized, username, imageUrl]);

    const userProfileButtonStyles: Partial<IButtonStyles> = {
        menuIcon: {
            display: 'none',
        },
        root: {
            paddingTop: '0px',
            paddingRight: '5px',
            paddingBottom: '0px',
            paddingLeft: '5px',
            maxHeight: '40px',
            minHeight: '40px',
            border: 'none',
            // Sit on the header chrome rather than the default button surface
            // so the persona button reads as part of the banner.
            backgroundColor: 'transparent',
        },
        rootHovered: {
            backgroundColor: theme.surfaces.chromeHover,
        },
        rootPressed: {
            backgroundColor: theme.surfaces.chromeHover,
        },
        rootExpanded: {
            backgroundColor: theme.surfaces.chromeHover,
        },
    };

    const loginButtonStyles: Partial<IButtonStyles> = {
        root: {
            backgroundColor: 'transparent',
            borderColor: theme.surfaces.chromeBorder,
            color: theme.semanticColors.bodyText,
        },
        rootHovered: {
            backgroundColor: theme.surfaces.chromeHover,
            color: theme.semanticColors.bodyText,
        },
        rootPressed: {
            backgroundColor: theme.surfaces.chromeHover,
            color: theme.semanticColors.bodyText,
        },
    };

    const handleUserLogin = () => {
        if (authContext.login) {
            authContext.login();
        }
    };

    const handleUserLogout = () => {
        if (authContext.logout) {
            authContext.logout();
        }
    };

    const oppositeTheme =
        themeContext.themeName && themeContext.themeName === 'dark'
            ? 'default'
            : 'dark';

    var items: IContextualMenuItem[] = [];
    if (themeContext.themeName) {
        items.push({
            key: 'themeChange',
            text: 'Switch to ' + oppositeTheme + ' theme',
            iconProps: { iconName: 'Color' },
            onClick: () => {
                themeContext.setTheme && themeContext.setTheme(oppositeTheme);
            },
        });
    }

    // My Roles modal (only shown if user has roles on their token)
    if (userRoles.length > 0) {
        items.push({
            key: 'myRoles',
            text: 'My Roles',
            iconProps: { iconName: 'Permissions' },
            onClick: () => setIsRolesModalOpen(true),
        });
    }

    // Privacy notice
    const privacyUrl = getConfig(GlobalConfigProperties.Privacy_url);
    if (privacyUrl) {
        items.push({
            key: 'privacy',
            text: 'Privacy + Terms',
            iconProps: { iconName: 'Shield' },
            onClick: () => {
                window.open(privacyUrl, '_blank', 'noopener,noreferrer');
            },
        });
    }

    if (authContext.logout) {
        items.push({
            key: 'logout',
            text: 'Logout',
            iconProps: { iconName: 'SignOut' },
            onClick: handleUserLogout,
        });
    }

    const menuProps: IContextualMenuProps = {
        shouldFocusOnMount: true,
        directionalHint: DirectionalHint.bottomRightEdge,
        directionalHintFixed: true,
        items: items,
    };

    return authContext.isUserAuthorized ? (
        <>
            <CompoundButton
                menuProps={menuProps}
                styles={userProfileButtonStyles}
            >
                <UserPersona
                    imageUrl={imageUrl}
                    name={
                        authContext.user?.name ??
                        (authContext.user?.idTokenClaims as HubIdTokenClaims)
                            ?.name
                    }
                    username={username}
                />
            </CompoundButton>
            <UserRolesModal
                isOpen={isRolesModalOpen}
                onDismiss={() => setIsRolesModalOpen(false)}
            />
        </>
    ) : authContext.login ? (
        <DefaultButton
            text="Login"
            onClick={handleUserLogin}
            styles={loginButtonStyles}
        />
    ) : null;
};
